import { Button } from '@/components/ui/button';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BackgroundImageUploader } from '@/components/BackgroundImageUploader';
import { useBackgroundImage } from '@/hooks/useBackgroundImage';
import heroImage from '@/assets/triathlon-finish-line.jpg';

interface HeroSectionProps {
  user: any;
  isAdmin: boolean;
  isAdminMode: boolean;
  onGetStarted: () => void;
  onViewSampleAndTemplates: () => void;
  onViewChecklists: () => void;
  hasUserChecklists: () => boolean;
  handleAddSection: () => void;
}

export const HeroSection = ({
  user,
  isAdmin,
  isAdminMode,
  onGetStarted,
  onViewSampleAndTemplates,
  onViewChecklists,
  hasUserChecklists,
  handleAddSection
}: HeroSectionProps) => {
  const navigate = useNavigate();
  // Skip loading background for non-admin users (saves 300-400ms on mobile)
  const { backgroundImage } = useBackgroundImage('home', false, !isAdminMode);

  return (
    <section className="relative min-h-[50vh] sm:min-h-[60vh] md:h-[65vh] lg:h-[70vh] overflow-hidden pt-16 md:pt-0">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={backgroundImage?.image_url || heroImage}
          alt="Triathlon athlete preparing for race day"
          className="w-full h-full"
          data-hero-image="true"
          style={{
            objectFit: 'cover',
            objectPosition: backgroundImage?.background_position || 'center center'
          }}
          loading="eager"
          fetchPriority="high"
        />
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/10" />
      </div>

      {/* Hero Content - Bottom Left */}
      <div className="relative h-full flex items-end pb-8 md:pb-20 px-4 md:px-16 max-w-[1280px] mx-auto">
        <div className="max-w-[640px]">
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-semibold text-white mb-4 sm:mb-6 leading-tight tracking-tight drop-shadow-2xl"
            style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}
          >
            Train Hard. <br />Pack Smart. <br />Race Confident.
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-3 md:mb-4 font-body max-w-[500px] drop-shadow-lg">
            Build your custom triathlon checklist in minutes
          </p>
          <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 md:mb-8 font-body max-w-[500px] drop-shadow-lg">
            Create custom checklists, track your progress, and ensure you're race-day ready
          </p>
          
          {/* CTA Buttons */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
            <Button 
              size="lg" 
              onClick={onGetStarted}
              variant="outline"
              className="h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg bg-white/10 text-white border-white/20 hover:bg-white/20 rounded-sm font-normal backdrop-blur-sm"
            >
              Getting Started
            </Button>
            <Button 
              size="lg" 
              onClick={onViewSampleAndTemplates}
              variant="outline"
              className="h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg bg-white/10 text-white border-white/20 hover:bg-white/20 rounded-sm font-normal backdrop-blur-sm"
            >
              View Sample and Templates
            </Button>
            <Button 
              size="lg" 
              onClick={handleAddSection}
              className="h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg bg-primary hover:bg-primary/90 text-white rounded-sm font-normal md:col-span-2"
            >
              Create Your Checklist
            </Button>
          </div>
        </div>
      </div>

      {/* Admin Image Upload (preserve existing functionality) */}
      {isAdmin && isAdminMode && (
        <div className="absolute top-4 right-4 z-10">
          <BackgroundImageUploader />
        </div>
      )}
    </section>
  );
};
